import { useState, useMemo, useCallback, useRef, useEffect, Component, type ReactNode } from 'react';
import { useFinancialFlows, useFinancialStats, useFinancialEntities } from '../../hooks/useApi';
import { sankey, sankeyLinkHorizontal, type SankeyNode, type SankeyLink } from 'd3-sankey';
import { scaleOrdinal } from 'd3-scale';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MoneyFlowProps {
  onSelectEntity: (id: string) => void;
  filters: Record<string, any>;
}

/** Extended node that carries our extra fields through d3-sankey layout. */
interface NodeExtra {
  name: string;
  entityId: string;
  entityType: string;
  isShell: boolean;
}

/** Extended link that carries our extra fields through d3-sankey layout. */
interface LinkExtra {
  source: number;
  target: number;
  value: number;
  flowType: string;
  flowId: string;
  amountDisplay: string;
  description: string;
}

type LayoutNode = SankeyNode<NodeExtra, LinkExtra>;
type LayoutLink = SankeyLink<NodeExtra, LinkExtra>;

// ---------------------------------------------------------------------------
// Tab type
// ---------------------------------------------------------------------------

type MoneyTab = 'overview' | 'flows' | 'entities' | 'banks';

const MONEY_TABS: { key: MoneyTab; label: string; icon: string }[] = [
  { key: 'overview', label: 'Overview', icon: '\u{1F4CA}' },
  { key: 'flows', label: 'Flows', icon: '\u{1F4B8}' },
  { key: 'entities', label: 'Entities', icon: '\u{1F3E2}' },
  { key: 'banks', label: 'Banks', icon: '\u{1F3E6}' },
];

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORY_COLORS: Record<string, string> = {
  person: '#5865F2',
  bank: '#3BA55C',
  shell_company: '#ED4245',
  trust: '#FAA61A',
  foundation: '#9B59B6',
  corporation: '#1ABC9C',
  government: '#E67E22',
  nonprofit: '#2ECC71',
  unknown: '#95A5A6',
};

const FALLBACK_PALETTE = [
  '#5865F2', '#ED4245', '#3BA55C', '#FAA61A', '#EB459E',
  '#9B59B6', '#1ABC9C', '#E67E22', '#2C3E50', '#95A5A6',
  '#E74C3C', '#3498DB', '#2ECC71', '#F1C40F', '#8E44AD',
];

const MARGIN = { top: 16, right: 180, bottom: 16, left: 180 };
const NODE_WIDTH = 18;
const NODE_PAD = 14;
const MIN_LINK_VALUE = 1000; // minimum for visibility in the sankey layout

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format a dollar amount into a human-readable string. */
function formatAmount(value: number): string {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

/** Truncate a name to maxLen characters with ellipsis. */
function truncate(s: string, maxLen: number): string {
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen - 1) + '\u2026';
}

/**
 * Color for an entity type.  Falls back to the ordinal palette index
 * so every node always gets a deterministic color.
 */
function entityColor(entityType: string, index: number): string {
  return CATEGORY_COLORS[entityType] ?? FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];
}

// ---------------------------------------------------------------------------
// Cycle detection & removal
// ---------------------------------------------------------------------------

interface DirectedEdge {
  from: number;
  to: number;
  value: number;
  flowType: string;
  flowId: string;
  amountDisplay: string;
  description: string;
}

/**
 * Given raw flows, build an acyclic graph suitable for d3-sankey:
 *
 * 1. Aggregate bidirectional flows between the same pair into a single
 *    net-direction edge (the direction of the larger total wins).
 * 2. Run DFS-based cycle detection on whatever remains and remove the
 *    smallest back-edge in every cycle found.
 */
function buildAcyclicGraph(
  flows: Array<{
    id: string;
    source_name: string;
    target_name: string;
    amount: number;
    flow_type: string;
    amount_display: string;
    description: string;
  }>,
  entityLookup: Map<string, { id: string; entity_type: string; is_shell: boolean }>,
): { nodes: NodeExtra[]; links: LinkExtra[] } {
  // -- Collect unique node names ------------------------------------------------
  const nameSet = new Set<string>();
  for (const f of flows) {
    if (f.source_name === f.target_name) continue; // self-loops
    nameSet.add(f.source_name);
    nameSet.add(f.target_name);
  }
  const nameArray = Array.from(nameSet);
  const nameIndex = new Map(nameArray.map((n, i) => [n, i]));

  const nodes: NodeExtra[] = nameArray.map((name) => {
    const ent = entityLookup.get(name);
    return {
      name,
      entityId: ent?.id ?? '',
      entityType: ent?.entity_type ?? 'unknown',
      isShell: ent?.is_shell ?? false,
    };
  });

  // -- Step 1: aggregate bidirectional pairs into net flows ----------------------
  // Key = "min##max" to normalise pair direction.
  const pairMap = new Map<
    string,
    { a: number; b: number; aToB: number; bToA: number; flowType: string; flowId: string; amountDisplay: string; description: string }
  >();

  for (const f of flows) {
    const si = nameIndex.get(f.source_name);
    const ti = nameIndex.get(f.target_name);
    if (si === undefined || ti === undefined || si === ti) continue;

    const lo = Math.min(si, ti);
    const hi = Math.max(si, ti);
    const key = `${lo}##${hi}`;

    let entry = pairMap.get(key);
    if (!entry) {
      entry = { a: lo, b: hi, aToB: 0, bToA: 0, flowType: f.flow_type, flowId: f.id, amountDisplay: f.amount_display, description: f.description };
      pairMap.set(key, entry);
    }

    if (si === lo) {
      entry.aToB += f.amount;
    } else {
      entry.bToA += f.amount;
    }
  }

  const edges: DirectedEdge[] = [];
  for (const p of pairMap.values()) {
    const net = p.aToB - p.bToA;
    if (net === 0) continue; // perfectly balanced -- drop
    if (net > 0) {
      edges.push({ from: p.a, to: p.b, value: net, flowType: p.flowType, flowId: p.flowId, amountDisplay: formatAmount(net), description: p.description });
    } else {
      edges.push({ from: p.b, to: p.a, value: -net, flowType: p.flowType, flowId: p.flowId, amountDisplay: formatAmount(-net), description: p.description });
    }
  }

  // -- Step 2: DFS cycle detection & back-edge removal --------------------------
  const adj = new Map<number, DirectedEdge[]>();
  for (const e of edges) {
    let list = adj.get(e.from);
    if (!list) { list = []; adj.set(e.from, list); }
    list.push(e);
  }

  const WHITE = 0, GRAY = 1, BLACK = 2;
  const color = new Map<number, number>();
  const backEdges = new Set<DirectedEdge>();

  function dfs(u: number) {
    color.set(u, GRAY);
    for (const e of adj.get(u) ?? []) {
      const c = color.get(e.to) ?? WHITE;
      if (c === GRAY) {
        // back-edge => cycle
        backEdges.add(e);
      } else if (c === WHITE) {
        dfs(e.to);
      }
    }
    color.set(u, BLACK);
  }

  for (let i = 0; i < nodes.length; i++) {
    if ((color.get(i) ?? WHITE) === WHITE) dfs(i);
  }

  // Build final links, excluding back-edges
  const links: LinkExtra[] = edges
    .filter((e) => !backEdges.has(e))
    .map((e) => ({
      source: e.from,
      target: e.to,
      value: Math.max(e.value, MIN_LINK_VALUE),
      flowType: e.flowType,
      flowId: e.flowId,
      amountDisplay: e.amountDisplay,
      description: e.description,
    }));

  return { nodes, links };
}

// ---------------------------------------------------------------------------
// Error Boundary
// ---------------------------------------------------------------------------

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class MoneyFlowErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="money-error-boundary">
            <div className="money-error-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ED4245" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 style={{ color: '#e0e0e0', margin: '12px 0 6px' }}>Financial Visualization Error</h3>
            <p style={{ color: '#999', fontSize: 14, maxWidth: 420, lineHeight: 1.5 }}>
              The money flow diagram encountered an error and could not render.
              This is usually caused by unusual data patterns. Try filtering to
              a specific entity or refreshing.
            </p>
            <pre style={{ color: '#ED4245', fontSize: 12, marginTop: 8, opacity: 0.7 }}>
              {this.state.error?.message}
            </pre>
            <button
              style={{
                marginTop: 16,
                padding: '8px 20px',
                background: '#5865F2',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 14,
              }}
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              Try Again
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Sankey SVG Component (React JSX, no raw DOM)
// ---------------------------------------------------------------------------

interface SankeyDiagramProps {
  nodes: LayoutNode[];
  links: LayoutLink[];
  width: number;
  height: number;
  selectedNode: string | null;
  onClickNode: (name: string, entityId: string) => void;
  onClickLink: (link: LayoutLink) => void;
}

function SankeyDiagram({ nodes, links, width, height, selectedNode, onClickNode, onClickLink }: SankeyDiagramProps) {
  const [hoveredLink, setHoveredLink] = useState<number | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const pathGen = sankeyLinkHorizontal();

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height="100%"
      style={{ display: 'block', overflow: 'visible' }}
    >
      {/* Links */}
      <g fill="none">
        {links.map((link, i) => {
          const srcNode = link.source as LayoutNode;
          const tgtNode = link.target as LayoutNode;
          const srcName = srcNode.name ?? '';
          const tgtName = tgtNode.name ?? '';
          const isHighlighted =
            hoveredLink === i ||
            hoveredNode === srcName ||
            hoveredNode === tgtName ||
            selectedNode === srcName ||
            selectedNode === tgtName;
          const isDimmed = (hoveredNode !== null || selectedNode !== null) && !isHighlighted;
          const strokeColor = entityColor(srcNode.entityType, srcNode.index ?? 0);

          return (
            <path
              key={`link-${i}`}
              d={pathGen(link as any) ?? ''}
              stroke={strokeColor}
              strokeOpacity={isDimmed ? 0.08 : isHighlighted ? 0.65 : 0.3}
              strokeWidth={Math.max((link as any).width ?? 1, 1.5)}
              style={{ cursor: 'pointer', transition: 'stroke-opacity 0.2s' }}
              onMouseEnter={() => setHoveredLink(i)}
              onMouseLeave={() => setHoveredLink(null)}
              onClick={() => onClickLink(link)}
            >
              <title>
                {srcName} {'\u2192'} {tgtName}: {(link as any).amountDisplay ?? formatAmount(link.value ?? 0)}
                {(link as any).description ? `\n${(link as any).description}` : ''}
              </title>
            </path>
          );
        })}
      </g>

      {/* Nodes */}
      <g>
        {nodes.map((node) => {
          const x0 = node.x0 ?? 0;
          const y0 = node.y0 ?? 0;
          const x1 = node.x1 ?? 0;
          const y1 = node.y1 ?? 0;
          const nodeH = Math.max(y1 - y0, 2);
          const nodeW = x1 - x0;
          const color = entityColor(node.entityType, node.index ?? 0);
          const isSelected = selectedNode === node.name;
          const isHovered = hoveredNode === node.name;
          const isDimmed = (hoveredNode !== null || selectedNode !== null) && !isSelected && !isHovered;

          // Label positioning: left side labels go right-to-left, right side go left-to-right
          const isLeftSide = x0 < width / 2;
          const labelX = isLeftSide ? x0 - 8 : x1 + 8;
          const labelAnchor = isLeftSide ? 'end' : 'start';
          const labelY = y0 + nodeH / 2;

          // Adaptive label length based on available margin
          const maxLabelLen = isLeftSide
            ? Math.floor((x0 - 8) / 6.5)
            : Math.floor((width - x1 - 8) / 6.5);
          const displayName = truncate(node.name, Math.max(maxLabelLen, 12));

          return (
            <g key={`node-${node.index}`}>
              {/* Node rect */}
              <rect
                x={x0}
                y={y0}
                width={nodeW}
                height={nodeH}
                fill={color}
                opacity={isDimmed ? 0.3 : isSelected || isHovered ? 1 : 0.85}
                rx={2}
                style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                onMouseEnter={() => setHoveredNode(node.name)}
                onMouseLeave={() => setHoveredNode(null)}
                onClick={() => onClickNode(node.name, node.entityId)}
              >
                <title>
                  {node.name}
                  {node.entityType !== 'unknown' ? ` (${node.entityType.replace(/_/g, ' ')})` : ''}
                  {node.isShell ? ' [Shell Company]' : ''}
                </title>
              </rect>

              {/* Shell company indicator */}
              {node.isShell && (
                <rect
                  x={x0}
                  y={y0}
                  width={nodeW}
                  height={nodeH}
                  fill="none"
                  stroke="#ED4245"
                  strokeWidth={2}
                  strokeDasharray="4 2"
                  rx={2}
                  pointerEvents="none"
                />
              )}

              {/* Label */}
              <text
                x={labelX}
                y={labelY}
                dy="0.35em"
                textAnchor={labelAnchor}
                fill={isDimmed ? '#666' : '#ddd'}
                fontSize={11}
                fontFamily="system-ui, -apple-system, sans-serif"
                fontWeight={isSelected ? 600 : 400}
                style={{ cursor: 'pointer', transition: 'fill 0.2s', pointerEvents: 'none' }}
              >
                {displayName}
              </text>

              {/* Small colored dot indicating entity type next to label */}
              {node.entityType !== 'unknown' && (
                <circle
                  cx={isLeftSide ? labelX - displayName.length * 6.5 - 8 : labelX + displayName.length * 6.5 + 8}
                  cy={labelY}
                  r={3}
                  fill={color}
                  opacity={isDimmed ? 0.3 : 0.8}
                  pointerEvents="none"
                />
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Stats Summary Bar
// ---------------------------------------------------------------------------

interface StatsSummaryProps {
  stats: {
    total_tracked_display: string;
    entity_count: number;
    flow_count: number;
    shell_company_count: number;
    bank_count: number;
  };
}

function StatsSummary({ stats }: StatsSummaryProps) {
  const items = [
    { label: 'Total Tracked', value: stats.total_tracked_display, accent: '#3BA55C' },
    { label: 'Entities', value: stats.entity_count?.toLocaleString(), accent: '#5865F2' },
    { label: 'Flows', value: stats.flow_count?.toLocaleString(), accent: '#FAA61A' },
    { label: 'Shell Companies', value: stats.shell_company_count?.toLocaleString(), accent: '#ED4245' },
    { label: 'Banks', value: stats.bank_count?.toLocaleString(), accent: '#1ABC9C' },
  ];

  return (
    <div className="money-stats-bar">
      {items.map((item) => (
        <div key={item.label} className="money-stat">
          <span className="money-stat-value" style={{ color: item.accent }}>{item.value}</span>
          <span className="money-stat-label">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview Tab
// ---------------------------------------------------------------------------

function OverviewTab({ stats, entities }: { stats: any; entities: any[] }) {
  if (!stats) {
    return (
      <div className="money-tab-loading">
        <div className="money-loading-spinner" />
        <span>Loading overview...</span>
      </div>
    );
  }

  // Group entities by type for summary
  const byType = useMemo(() => {
    const map = new Map<string, number>();
    for (const ent of entities) {
      map.set(ent.entity_type, (map.get(ent.entity_type) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [entities]);

  return (
    <div className="money-tab-content">
      <StatsSummary stats={stats} />

      <div className="money-overview-grid">
        <div className="money-overview-card">
          <h4>Entity Types</h4>
          <div className="money-overview-list">
            {byType.map(([type, count]) => (
              <div key={type} className="money-overview-item">
                <span
                  className="money-overview-dot"
                  style={{ backgroundColor: CATEGORY_COLORS[type] || '#95A5A6' }}
                />
                <span className="money-overview-type">{type.replace(/_/g, ' ')}</span>
                <span className="money-overview-count">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="money-overview-card">
          <h4>Key Metrics</h4>
          <div className="money-overview-list">
            <div className="money-overview-item">
              <span className="money-overview-type">Total Amount Tracked</span>
              <span className="money-overview-count" style={{ color: '#3BA55C' }}>
                {stats.total_tracked_display}
              </span>
            </div>
            <div className="money-overview-item">
              <span className="money-overview-type">Financial Flows</span>
              <span className="money-overview-count">{stats.flow_count?.toLocaleString()}</span>
            </div>
            <div className="money-overview-item">
              <span className="money-overview-type">Shell Companies</span>
              <span className="money-overview-count" style={{ color: '#ED4245' }}>
                {stats.shell_company_count?.toLocaleString()}
              </span>
            </div>
            <div className="money-overview-item">
              <span className="money-overview-type">Banks Involved</span>
              <span className="money-overview-count" style={{ color: '#1ABC9C' }}>
                {stats.bank_count?.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entities Tab
// ---------------------------------------------------------------------------

function EntitiesTab({ entities, onSelectEntity }: { entities: any[]; onSelectEntity: (id: string) => void }) {
  const [filterType, setFilterType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = useMemo(() => {
    let list = entities;
    if (filterType) {
      list = list.filter((e) => e.entity_type === filterType);
    }
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      list = list.filter((e) => e.name.toLowerCase().includes(lower));
    }
    return list;
  }, [entities, filterType, searchTerm]);

  const types = useMemo(() => {
    const set = new Set<string>();
    for (const e of entities) set.add(e.entity_type);
    return Array.from(set).sort();
  }, [entities]);

  return (
    <div className="money-tab-content">
      <div className="money-tab-filters">
        <div className="money-search-wrapper">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="money-search"
            placeholder="Search entities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="money-search-clear" onClick={() => setSearchTerm('')}>{'\u00D7'}</button>
          )}
        </div>
        <select
          className="money-tab-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="">All types</option>
          {types.map((t) => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>
      <div className="money-entity-count">{filtered.length} entities</div>
      <div className="money-entity-list">
        {filtered.map((entity) => {
          const chipColor = CATEGORY_COLORS[entity.entity_type] ?? '#95A5A6';
          return (
            <button
              key={entity.id}
              className="money-entity-row"
              onClick={() => onSelectEntity(entity.id)}
            >
              <span className="money-entity-dot" style={{ backgroundColor: chipColor }} />
              <span className="money-entity-name">{entity.name}</span>
              <span className="money-entity-type">{entity.entity_type.replace(/_/g, ' ')}</span>
              {entity.is_shell && <span className="shell-badge">Shell</span>}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="money-entity-empty">No entities found</div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Banks Tab
// ---------------------------------------------------------------------------

function BanksTab({ entities, onSelectEntity }: { entities: any[]; onSelectEntity: (id: string) => void }) {
  const banks = useMemo(() => {
    return entities.filter((e) => e.entity_type === 'bank');
  }, [entities]);

  return (
    <div className="money-tab-content">
      <div className="money-entity-count">{banks.length} banks identified</div>
      <div className="money-entity-list">
        {banks.map((bank) => (
          <button
            key={bank.id}
            className="money-entity-row money-entity-row--bank"
            onClick={() => onSelectEntity(bank.id)}
          >
            <span className="money-entity-dot" style={{ backgroundColor: '#3BA55C' }} />
            <span className="money-entity-name">{bank.name}</span>
            {bank.is_shell && <span className="shell-badge">Shell</span>}
          </button>
        ))}
        {banks.length === 0 && (
          <div className="money-entity-empty">No banks found in current data</div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flows Tab (Sankey diagram)
// ---------------------------------------------------------------------------

function FlowsTab({
  flows,
  entities,
  entityLookup,
  isLoading,
  isError,
  error,
  onSelectEntity,
}: {
  flows: any[];
  entities: any[];
  entityLookup: Map<string, { id: string; entity_type: string; is_shell: boolean }>;
  isLoading: boolean;
  isError: boolean;
  error: any;
  onSelectEntity: (id: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [entityFilter, setEntityFilter] = useState('');
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [dimensions, setDimensions] = useState({ width: 900, height: 520 });

  // --- Responsive sizing ---
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        const h = Math.max(360, Math.min(w * 0.6, 700));
        setDimensions({ width: Math.max(w, 400), height: h });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // --- Build acyclic sankey data ---
  const sankeyResult = useMemo(() => {
    if (!flows.length) return null;

    try {
      const { nodes, links } = buildAcyclicGraph(flows, entityLookup);
      if (nodes.length === 0 || links.length === 0) return null;

      const { width, height } = dimensions;
      const sankeyGen = sankey<NodeExtra, LinkExtra>()
        .nodeWidth(NODE_WIDTH)
        .nodePadding(NODE_PAD)
        .extent([
          [MARGIN.left, MARGIN.top],
          [width - MARGIN.right, height - MARGIN.bottom],
        ]);

      const result = sankeyGen({
        nodes: nodes.map((d) => ({ ...d })),
        links: links.map((d) => ({ ...d })),
      });

      return result;
    } catch (err) {
      console.error('[MoneyFlow] Sankey layout error:', err);
      return null;
    }
  }, [flows, entityLookup, dimensions]);

  // --- Handlers ---
  const handleNodeClick = useCallback(
    (name: string, entityId: string) => {
      if (selectedNode === name) {
        setSelectedNode(null);
        setEntityFilter('');
      } else {
        setSelectedNode(name);
        setEntityFilter(name);
        if (entityId) {
          onSelectEntity(entityId);
        }
      }
    },
    [selectedNode, onSelectEntity],
  );

  const handleLinkClick = useCallback(
    (link: LayoutLink) => {
      const src = (link.source as LayoutNode).entityId;
      if (src) onSelectEntity(src);
    },
    [onSelectEntity],
  );

  const handleClearFilter = useCallback(() => {
    setEntityFilter('');
    setSelectedNode(null);
  }, []);

  // --- Legend ---
  const visibleTypes = useMemo(() => {
    if (!sankeyResult) return [];
    const types = new Set<string>();
    for (const n of sankeyResult.nodes) {
      if ((n as any).entityType && (n as any).entityType !== 'unknown') {
        types.add((n as any).entityType);
      }
    }
    return Array.from(types).sort();
  }, [sankeyResult]);

  return (
    <div className="money-flows-tab">
      {/* Filter bar */}
      <div className="money-flows-filter">
        <div className="money-search-wrapper">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="money-search"
            placeholder="Filter by entity name..."
            value={entityFilter}
            onChange={(e) => {
              setEntityFilter(e.target.value);
              if (!e.target.value) setSelectedNode(null);
            }}
          />
          {entityFilter && (
            <button className="money-search-clear" onClick={handleClearFilter} title="Clear filter">
              {'\u00D7'}
            </button>
          )}
        </div>

        {/* Legend */}
        {visibleTypes.length > 0 && (
          <div className="money-legend">
            {visibleTypes.map((type) => (
              <span key={type} className="money-legend-item">
                <span
                  className="money-legend-swatch"
                  style={{ backgroundColor: CATEGORY_COLORS[type] ?? '#95A5A6' }}
                />
                {type.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Sankey diagram */}
      <div className="money-sankey" ref={containerRef}>
        {isLoading ? (
          <div className="money-loading">
            <div className="money-loading-spinner" />
            <span>Loading financial data...</span>
          </div>
        ) : isError ? (
          <div className="money-error">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ED4245" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p>Failed to load financial flows{error instanceof Error ? `: ${error.message}` : ''}</p>
            <button className="money-retry-btn" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        ) : flows.length === 0 ? (
          <div className="money-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.5">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
              <path d="M12 6v6l4 2" />
            </svg>
            <p style={{ color: '#999', marginTop: 12 }}>
              {entityFilter
                ? `No financial flows found for "${entityFilter}"`
                : 'No financial flows to display'}
            </p>
            {entityFilter && (
              <button className="money-retry-btn" onClick={handleClearFilter}>
                Clear Filter
              </button>
            )}
          </div>
        ) : sankeyResult ? (
          <SankeyDiagram
            nodes={sankeyResult.nodes as LayoutNode[]}
            links={sankeyResult.links as LayoutLink[]}
            width={dimensions.width}
            height={dimensions.height}
            selectedNode={selectedNode}
            onClickNode={handleNodeClick}
            onClickLink={handleLinkClick}
          />
        ) : (
          <div className="money-empty">
            <p style={{ color: '#999' }}>
              Could not generate flow diagram from current data. Try a different filter.
            </p>
          </div>
        )}
      </div>

      {/* Top entities chip bar */}
      {entities.length > 0 && (
        <div className="money-entities">
          <h3>Top Entities</h3>
          <div className="entity-chips">
            {entities.slice(0, 16).map((entity) => {
              const isActive = entityFilter === entity.name;
              const chipColor = CATEGORY_COLORS[entity.entity_type] ?? '#95A5A6';
              return (
                <button
                  key={entity.id}
                  className={`entity-chip ${isActive ? 'entity-chip-active' : ''}`}
                  style={isActive ? { borderColor: chipColor, background: `${chipColor}22` } : undefined}
                  onClick={() => {
                    if (isActive) {
                      handleClearFilter();
                    } else {
                      setEntityFilter(entity.name);
                      setSelectedNode(entity.name);
                    }
                  }}
                >
                  <span
                    className="entity-chip-dot"
                    style={{ backgroundColor: chipColor }}
                  />
                  <span className="entity-chip-name">{truncate(entity.name, 24)}</span>
                  <span className="entity-chip-type">{entity.entity_type.replace(/_/g, ' ')}</span>
                  {entity.is_shell && <span className="shell-badge">Shell</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

function MoneyFlowInner({ onSelectEntity, filters }: MoneyFlowProps) {
  const [activeTab, setActiveTab] = useState<MoneyTab>('flows');

  // --- Data fetching ---
  const { data: statsData } = useFinancialStats();
  const { data: flowsData, isLoading, isError, error } = useFinancialFlows({
    page: 1,
    per_page: 100,
  });
  const { data: entitiesData } = useFinancialEntities({ per_page: 50 });

  const flows = flowsData?.data ?? [];
  const stats = statsData?.data;
  const entities = entitiesData?.data ?? [];

  // --- Entity lookup for enrichment ---
  const entityLookup = useMemo(() => {
    const map = new Map<string, { id: string; entity_type: string; is_shell: boolean }>();
    for (const ent of entities) {
      map.set(ent.name, { id: ent.id, entity_type: ent.entity_type, is_shell: ent.is_shell });
    }
    return map;
  }, [entities]);

  // --- Render ---
  return (
    <div className="money-flow-container">
      {/* Tab navigation */}
      <div className="money-tab-bar">
        {MONEY_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`money-tab ${activeTab === tab.key ? 'money-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <span className="money-tab-icon">{tab.icon}</span>
            <span className="money-tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="money-tab-body">
        {activeTab === 'overview' && (
          <OverviewTab stats={stats} entities={entities} />
        )}
        {activeTab === 'flows' && (
          <FlowsTab
            flows={flows}
            entities={entities}
            entityLookup={entityLookup}
            isLoading={isLoading}
            isError={isError}
            error={error}
            onSelectEntity={onSelectEntity}
          />
        )}
        {activeTab === 'entities' && (
          <EntitiesTab entities={entities} onSelectEntity={onSelectEntity} />
        )}
        {activeTab === 'banks' && (
          <BanksTab entities={entities} onSelectEntity={onSelectEntity} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exported component wrapped in error boundary
// ---------------------------------------------------------------------------

export default function MoneyFlow(props: MoneyFlowProps) {
  return (
    <MoneyFlowErrorBoundary>
      <MoneyFlowInner {...props} />
    </MoneyFlowErrorBoundary>
  );
}
